import { useOptionalTour } from "@bcc/contexts/tour-context";
import { useEffect, useState } from "react";

export interface DemoDataModule<T> {
  /** True while a tour that uses sample rows is running and its fixtures have not failed to load */
  isActive: boolean;
  /** The loaded fixtures; undefined until the first activation finishes loading (or while inactive) */
  module: T | undefined;
}

/**
 * Loads a demo-data module the first time the tour starts using sample rows, so
 * roasters who never take the tour never download the fixtures. `load` must be a
 * stable function (e.g. `() => import("./orders.demo")` declared at module scope).
 */
export function useDemoDataModule<T>(load: () => Promise<T>): DemoDataModule<T> {
  const isTourDemoActive = useOptionalTour()?.isDemoDataActive ?? false;
  const [module, setModule] = useState<T>();
  const [hasFailed, setHasFailed] = useState(false);
  // A fixture chunk that cannot download (e.g. removed by a deploy) hands the page back its real data instead of skeleton rows
  const isActive = isTourDemoActive && !hasFailed;

  useEffect(() => {
    if (!isActive || module !== undefined) return;
    let isCancelled = false;
    load().then(
      (loaded) => {
        if (!isCancelled) setModule(loaded);
      },
      () => {
        if (!isCancelled) setHasFailed(true);
      }
    );
    return () => {
      isCancelled = true;
    };
  }, [isActive, module, load]);

  return { isActive, module: isActive ? module : undefined };
}
